import type {
  ClaudeFamily,
  ModelCatalogView,
  ModelRoute,
  ModelSeatKey,
  SeatPin,
  SeatPricing,
} from '@matter_extractor/shared';
import { MODEL_SEAT_KEYS, claudeFamilyOf, claudeModelKey } from '@matter_extractor/shared';

/**
 * The models the Réglages screen offers as TILES (§11.1).
 *
 * Since #175 the Claude tiles come from the PROVIDERS: the backend reads
 * Anthropic's and OpenRouter's model lists and serves the newest model of each
 * family per door (`GET /api/models/catalog`). Until then this file WAS the
 * list, and a model release was one deploy away from the screen - which is how
 * it still offered Sonnet 5 a week after 5.5, and the problem the models screen
 * exists to remove.
 *
 * What stays here is what is EDITORIAL - a pace per family, which tile is
 * recommended - plus the list the screen falls back on when a door was not read
 * (a stub deploy, no key, a provider that did not answer). The fallback is
 * written for today's models and will age; the screen says when it is showing
 * it, so an aged list is visible rather than trusted.
 *
 * The catalogue is NOT a whitelist. Free entry stays one link away, and an
 * unknown model is still accepted with a warning (P2). What the tiles remove
 * is the TYPO - before BUILD-835, `claude-sonet-5` was a 500 on the first
 * testeur's second message.
 */

export type ModelTile = {
  /** The id exactly as its door names it. */
  id: string;
  /** What the team calls it. Never the id - the id is shown underneath. */
  label: string;
  /** The door that serves this spelling. A tile belongs to exactly one. */
  route: ModelRoute;
  /** The endpoint this tile pins, when it names one. Null lets the router choose. */
  provider: string | null;
  /** Which seats may run it. */
  seats: readonly ModelSeatKey[];
  /** Three paces, as the mockup shows them. Editorial, not measured. */
  pace: 'posé' | 'rapide' | 'très rapide';
  /** At most one per (seat, route) - the tile somebody should pick by default. */
  recommended: boolean;
  /**
   * `claudeModelKey`: equal on both doors for the same weights, null for a
   * model that is not Claude. It is what a door change follows - the model
   * moves with the seat rather than being left behind in a spelling the new
   * door does not know, which would be a `route-model-mismatch`, i.e. a 404 on
   * every call of that seat.
   */
  key: string | null;
  /**
   * The published rate a provider list carries, in euros. Null when this tile
   * knows none: the seat then falls back on the grid the params view serves.
   */
  pricing: SeatPricing | null;
};

export type ModelCatalog = readonly ModelTile[];

/**
 * Every seat may run every tile since #61, and since #149 that is read off
 * `MODEL_SEAT_KEYS`: a hand-written list that forgot a seat would draw its
 * card with no tile at all.
 */
const EVERY_SEAT: readonly ModelSeatKey[] = MODEL_SEAT_KEYS;

const PACE: Record<ClaudeFamily, ModelTile['pace']> = {
  sonnet: 'rapide',
  opus: 'posé',
  fable: 'posé',
  haiku: 'très rapide',
};

/**
 * The row order, the same on every card and every door: row N of one seat is
 * the same family as row N of the next, so three seats compare line by line
 * (BUILD-925).
 */
const FAMILY_ORDER: readonly ClaudeFamily[] = ['sonnet', 'opus', 'fable', 'haiku'];

/** The family a seat should land on by default: the conductor since D1. */
const RECOMMENDED_FAMILY: ClaudeFamily = 'sonnet';

function claudeTile(
  id: string,
  label: string,
  route: ModelRoute,
  pricing: SeatPricing | null = null,
): ModelTile {
  const family = claudeFamilyOf(id);
  return {
    id,
    label,
    route,
    // The router serves Claude from Anthropic's own endpoint: pinned, so a
    // silent re-route onto another host is not a thing a tile can do.
    provider: route === 'openrouter' ? 'anthropic' : null,
    seats: EVERY_SEAT,
    pace: family === null ? 'rapide' : PACE[family],
    recommended: false,
    key: claudeModelKey(id),
    pricing,
  };
}

/**
 * What the screen shows when a door was not read. Written for the 5.5
 * generation (#175); the rate comes from the grid the params view serves.
 */
const FALLBACK_CLAUDE: readonly ModelTile[] = [
  claudeTile('claude-sonnet-5-5', 'Claude Sonnet 5.5', 'anthropic'),
  claudeTile('claude-opus-5-5', 'Claude Opus 5.5', 'anthropic'),
  claudeTile('claude-fable-5-1', 'Claude Fable 5.1', 'anthropic'),
  claudeTile('claude-haiku-4-5-20251001', 'Claude Haiku 4.5', 'anthropic'),
  claudeTile('anthropic/claude-sonnet-5.5', 'Claude Sonnet 5.5', 'openrouter'),
  claudeTile('anthropic/claude-opus-5.5', 'Claude Opus 5.5', 'openrouter'),
  claudeTile('anthropic/claude-fable-5.1', 'Claude Fable 5.1', 'openrouter'),
  claudeTile('anthropic/claude-haiku-4.5', 'Claude Haiku 4.5', 'openrouter'),
];

/**
 * The one router tile that is not Claude, offered whatever the lists say.
 *
 * Open weights, retained by the smoke test of the ADR of 2026-08-04. The
 * endpoint stays pinned for the reason it always was - the same open weights
 * are served fp8 by some providers and bf16 by others, and a silent re-route
 * changes what a seat actually ran on without anybody seeing it. No key: it
 * has no direct-API existence, so a door change never follows it.
 */
const MISTRAL_SMALL: ModelTile = {
  id: 'mistralai/mistral-small-2603',
  label: 'Mistral Small',
  route: 'openrouter',
  provider: 'mistral',
  seats: EVERY_SEAT,
  pace: 'très rapide',
  recommended: false,
  key: null,
  pricing: null,
};

/** « Claude Sonnet 5 » from `claude-sonnet-5`, for a pin no list names any more. */
function labelOfKey(key: string): string {
  const [, family = '', major = '', minor] = key.split('-');
  const version = minor === undefined ? major : `${major}.${minor}`;
  return `Claude ${family.charAt(0).toUpperCase()}${family.slice(1)} ${version}`;
}

function familyRank(tile: ModelTile): number {
  const family = claudeFamilyOf(tile.id);
  return family === null ? FAMILY_ORDER.length : FAMILY_ORDER.indexOf(family);
}

/**
 * The tiles of this deploy: the providers' lists where a door was read, the
 * fallback where it was not, plus a tile for every Claude model a seat is
 * pinned to and no list names any more.
 *
 * That last part is what keeps the screen honest the day a new version ships:
 * a seat still running Sonnet 5 shows « Claude Sonnet 5 » checked, with 5.5
 * above it, rather than a free-entry field claiming somebody typed the id by
 * hand. A configuration this repository actually produces has to be
 * expressible in tiles, or the free-entry field stays open on that seat
 * forever - which is the state the tiles exist to end.
 */
export function catalogFrom(
  view: ModelCatalogView | undefined,
  pinned: readonly SeatPin[] = [],
): ModelCatalog {
  const tiles: ModelTile[] = [];
  for (const route of ['anthropic', 'openrouter'] as const) {
    const listed =
      view?.doors[route] === 'live'
        ? view.entries
            .filter((entry) => entry.route === route)
            .map((entry) => claudeTile(entry.model, entry.label, route, entry.pricing))
        : [];
    tiles.push(...(listed.length > 0 ? listed : FALLBACK_CLAUDE.filter((t) => t.route === route)));
  }
  for (const pin of pinned) {
    const key = claudeModelKey(pin.model);
    if (key === null || tiles.some((tile) => tile.id === pin.model && tile.route === pin.route)) {
      continue;
    }
    // Only a spelling of THIS door: a router id pinned on the direct door is a
    // mismatch the free-entry field has to show, not a tile to legitimise.
    if ((pin.route === 'openrouter') !== pin.model.startsWith('anthropic/')) continue;
    // The pin's own endpoint: a migrated narrator sits on the router's Sonnet
    // 4.5 with none pinned, and a tile claiming `anthropic` would not match it.
    tiles.push({ ...claudeTile(pin.model, labelOfKey(key), pin.route), provider: pin.provider });
  }
  tiles.push(MISTRAL_SMALL);

  // Stable sort: within a family, the providers' newest-first order holds and
  // a pinned older version lands under the newer one.
  const ordered = tiles
    .map((tile, index) => ({ tile, index }))
    .sort(
      (left, right) => familyRank(left.tile) - familyRank(right.tile) || left.index - right.index,
    )
    .map(({ tile }) => tile);

  // One recommended tile per door: the first of the recommended family.
  return ordered.map((tile, index) => ({
    ...tile,
    recommended:
      claudeFamilyOf(tile.id) === RECOMMENDED_FAMILY &&
      ordered.findIndex(
        (other) => other.route === tile.route && claudeFamilyOf(other.id) === RECOMMENDED_FAMILY,
      ) === index,
  }));
}

/** The tiles a seat may show behind one door, in catalogue order. */
export function tilesFor(
  catalog: ModelCatalog,
  seat: ModelSeatKey,
  route: ModelRoute,
): ModelTile[] {
  return catalog.filter((tile) => tile.route === route && tile.seats.includes(seat));
}

/**
 * The tile a pin corresponds to, or null when the operator typed something
 * else.
 *
 * The ENDPOINT is part of the match and not only the id: the same routed model
 * pinned to another provider is a different serve, so highlighting the tile
 * would claim a pin the seat does not carry.
 */
export function tileOf(
  catalog: ModelCatalog,
  pin: { route: ModelRoute; model: string; provider: string | null },
): ModelTile | null {
  return (
    catalog.find(
      (tile) => tile.id === pin.model && tile.route === pin.route && tile.provider === pin.provider,
    ) ?? null
  );
}

/**
 * What a seat should land on when its door moves and the catalogue holds no
 * equivalent of what it was running.
 *
 * The recommended tile of the target door, and never « keep the id »: Mistral
 * Small has no direct-API existence, so keeping its id would put a router
 * spelling on the direct door in ONE CLICK, which is a 404 on every call of
 * that seat and only a warning at the save. It applies to tiles only - a
 * hand-typed id is never rewritten, because nobody can translate an id nobody
 * declared.
 */
export function recommendedFor(
  catalog: ModelCatalog,
  seat: ModelSeatKey,
  route: ModelRoute,
): ModelTile | null {
  const tiles = tilesFor(catalog, seat, route);
  return tiles.find((tile) => tile.recommended) ?? tiles[0] ?? null;
}

/**
 * The same model on the other door, when this one is a tile and the other door
 * offers the same weights.
 *
 * Null on anything hand-typed, and that is the honest answer: nobody can
 * translate an id nobody declared, and guessing would rewrite what the
 * operator wrote.
 */
export function twinOnRoute(
  catalog: ModelCatalog,
  pin: { route: ModelRoute; model: string; provider: string | null },
  route: ModelRoute,
): ModelTile | null {
  const current = tileOf(catalog, pin);
  if (current === null || current.key === null || current.route === route) return null;
  return catalog.find((tile) => tile.route === route && tile.key === current.key) ?? null;
}

/**
 * The sentence under the seats that says where the tiles come from.
 *
 * Said because the fallback AGES: a list read an hour ago and a list written
 * into this file weeks ago look the same, and only one of them is today's.
 */
export function catalogSourceSentence(view: ModelCatalogView | undefined): string {
  if (view === undefined) return 'Liste des modèles en cours de lecture chez les fournisseurs.';
  const { anthropic, openrouter } = view.doors;
  if (anthropic === 'stub' && openrouter === 'stub') {
    return 'Mode bouchonné : les tuiles sont la liste intégrée au dispositif, aucun fournisseur n’est lu.';
  }
  const read = [
    ...(anthropic === 'live' ? ['Anthropic'] : []),
    ...(openrouter === 'live' ? ['OpenRouter'] : []),
  ];
  const missed = [
    ...(anthropic === 'live' ? [] : ['API directe']),
    ...(openrouter === 'live' ? [] : ['routeur']),
  ];
  if (read.length === 0) {
    return 'Les fournisseurs n’ont pas pu être lus : les tuiles sont la liste intégrée au dispositif, qui peut dater.';
  }
  const when = view.fetchedAt === null ? '' : ` ${readSince(view.fetchedAt)}`;
  const partial =
    missed.length === 0
      ? ''
      : ` Liste intégrée au dispositif pour ${missed.join(' et ')}, qui peut dater.`;
  return `Derniers modèles lus chez ${read.join(' et ')}${when}.${partial}`;
}

/** « il y a 12 min », written by hand: `Intl` answers in the machine's locale. */
function readSince(fetchedAt: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - Date.parse(fetchedAt)) / 60_000));
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  return `il y a ${Math.round(minutes / 60)} h`;
}
